import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import moment from 'moment';
import { Link } from 'react-router-dom';
import {
  Send,
  Clock,
  Heart,
  Eye,
  MessageSquare,
  ChevronRight,
  Loader2,
  CalendarDays,
  Share2,
  Flame,
  Megaphone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { getPostText } from '../../utils/general';
import { API_BASE_URL } from '../../config';
import { cn } from "../../lib/utils";
import { usePostModal } from '../../contexts/PostModalContext';
import { PublishStatusBadge } from '../PublishStatusBadge';

interface ScheduleInfo {
  id: string;
  status: string;
  scheduledAt: string;
  platform: string;
  handle?: string;
  accountName?: string;
  externalPostId?: string;
  externalPostUrl?: string;
  result?: string;
  likes: number;
  views: number;
  comments: number;
  shares: number;
}

interface WeeklyPost {
  id: string;
  textEn?: string;
  textEs?: string;
  translations?: Record<string, { text: string }>;
  createdAt: string;
  game: { name: string };
  media: Array<{ filePath: string; thumbnailPath?: string | null; mediaType: string }>;
  schedules: ScheduleInfo[];
}

export default function WeeklySummary() {
  const token = localStorage.getItem('token');
  const { openPostModal } = usePostModal();

  const { data, isLoading } = useQuery({
    queryKey: ['weekly-summary'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/posts/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const posts = Array.isArray(response.data) ? response.data : (response.data.posts || []);

      const weekStart = moment().startOf('isoWeek');
      const weekEnd = moment().endOf('isoWeek');

      // Separate into sent this week and scheduled (future pending)
      const sent: WeeklyPost[] = [];
      const scheduled: WeeklyPost[] = [];

      for (const post of posts) {
        const schedules = (post.schedules || []).map((s: any) => ({
          id: s.id,
          status: s.status,
          scheduledAt: s.scheduledAt,
          platform: s.socialAccount?.platform || 'unknown',
          handle: s.socialAccount?.handle || '',
          accountName: s.socialAccount?.name || s.socialAccount?.handle || '',
          externalPostId: s.externalPostId,
          externalPostUrl: s.externalPostUrl,
          result: s.result,
          likes: s.likes || 0,
          views: s.views || 0,
          comments: s.comments || 0,
          shares: s.shares || 0,
        }));

        // Sent: completed this week
        const sentSchedules = schedules.filter((s: ScheduleInfo) =>
          s.status === 'COMPLETED' &&
          moment(s.scheduledAt).isBetween(weekStart, weekEnd, undefined, '[]')
        );

        if (sentSchedules.length > 0) {
          sent.push({
            id: post.id,
            textEn: post.textEn,
            textEs: post.textEs,
            translations: post.translations,
            createdAt: post.createdAt,
            game: post.game,
            media: post.media || [],
            schedules: sentSchedules,
          });
        }

        // Scheduled: pending/processing in the future
        const pendingSchedules = schedules.filter((s: ScheduleInfo) =>
          (s.status === 'PENDING' || s.status === 'PROCESSING') &&
          moment(s.scheduledAt).isAfter(moment())
        );

        if (pendingSchedules.length > 0) {
          scheduled.push({
            id: post.id,
            textEn: post.textEn,
            textEs: post.textEs,
            translations: post.translations,
            createdAt: post.createdAt,
            game: post.game,
            media: post.media || [],
            schedules: pendingSchedules,
          });
        }
      }

      // Aggregate stats
      const totalLikes = sent.reduce((sum, p) => sum + p.schedules.reduce((s, sc) => s + sc.likes, 0), 0);
      const totalViews = sent.reduce((sum, p) => sum + p.schedules.reduce((s, sc) => s + sc.views, 0), 0);
      const totalComments = sent.reduce((sum, p) => sum + p.schedules.reduce((s, sc) => s + sc.comments, 0), 0);
      const totalShares = sent.reduce((sum, p) => sum + p.schedules.reduce((s, sc) => s + sc.shares, 0), 0);
      const totalPlatforms = sent.reduce((sum, p) => sum + p.schedules.length, 0);

      return { sent, scheduled, totalLikes, totalViews, totalComments, totalShares, totalPlatforms };
    },
    enabled: !!token,
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <Card className="bg-card border-border shadow-xl overflow-hidden mt-6">
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-primary/50" />
        </CardContent>
      </Card>
    );
  }

  if (!data || (data.sent.length === 0 && data.scheduled.length === 0)) {
    return (
      <Card className="bg-card border-border shadow-xl overflow-hidden mt-6">
        <CardHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
              <div className="p-1.5 bg-primary/20 rounded-lg">
                <Flame className="h-4 w-4 text-primary" />
              </div>
              This Week
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6 flex flex-col items-center text-center gap-3">
          <div className="p-3 bg-muted/50 rounded-full">
            <Megaphone className="h-6 w-6 text-muted-foreground/60" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Nothing posted yet this week</p>
            <p className="text-xs text-muted-foreground mt-1">Keep your audience engaged — share something today!</p>
          </div>
          <button
            onClick={() => openPostModal()}
            className="mt-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-black uppercase tracking-widest rounded-lg hover:bg-primary/90 transition-colors"
          >
            Post now
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border shadow-xl overflow-hidden mt-6">
      <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
            <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
              <Flame className="h-4 w-4 text-primary" />
            </div>
            This Week
          </CardTitle>
          <Link
            to="/posts"
            className="text-[10px] font-black text-primary hover:text-primary/80 flex items-center gap-1 uppercase tracking-widest"
          >
            Posts & Agenda <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Aggregate Stats */}
        {data.sent.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-muted/60 rounded-lg p-2 text-center border border-border/50">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Heart className="h-2.5 w-2.5 text-red-500" />
              </div>
              <span className="text-sm font-black text-foreground">{data.totalLikes}</span>
              <p className="text-[7px] font-black text-muted-foreground uppercase">Likes</p>
            </div>
            <div className="bg-muted/60 rounded-lg p-2 text-center border border-border/50">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Eye className="h-2.5 w-2.5 text-primary" />
              </div>
              <span className="text-sm font-black text-foreground">{data.totalViews}</span>
              <p className="text-[7px] font-black text-muted-foreground uppercase">Views</p>
            </div>
            <div className="bg-muted/60 rounded-lg p-2 text-center border border-border/50">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <MessageSquare className="h-2.5 w-2.5 text-primary" />
              </div>
              <span className="text-sm font-black text-foreground">{data.totalComments}</span>
              <p className="text-[7px] font-black text-muted-foreground uppercase">Comments</p>
            </div>
            <div className="bg-muted/60 rounded-lg p-2 text-center border border-border/50">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Send className="h-2.5 w-2.5 text-green-500" />
              </div>
              <span className="text-sm font-black text-foreground">{data.totalPlatforms}</span>
              <p className="text-[7px] font-black text-muted-foreground uppercase">Published</p>
            </div>
          </div>
        )}

        {/* Scheduled Posts */}
        {data.scheduled.length > 0 && (
          <div className="space-y-2">
            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <Clock className="h-2.5 w-2.5" />
              Scheduled
            </p>
            {data.scheduled.slice(0, 3).map((post) => (
              <PostRow key={`sched-${post.id}`} post={post} type="scheduled" />
            ))}
          </div>
        )}

        {/* Sent Posts */}
        {data.sent.length > 0 && (
          <div className="space-y-2">
            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <Send className="h-2.5 w-2.5" />
              Sent this week
            </p>
            {data.sent.slice(0, 5).map((post) => (
              <PostRow key={`sent-${post.id}`} post={post} type="sent" />
            ))}
            {data.sent.length > 5 && (
              <Link to="/posts" className="block text-center text-[10px] font-bold text-muted-foreground hover:text-primary transition-colors py-1">
                +{data.sent.length - 5} more posts
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PostRow({ post, type }: { post: WeeklyPost; type: 'sent' | 'scheduled' }) {
  const token = localStorage.getItem('token');
  const text = getPostText(post);
  const thumbnail = post.media?.[0]?.thumbnailPath || post.media?.[0]?.filePath;

  return (
    <Link
      to={`/posts/${post.id}`}
      className={cn(
        "flex items-center gap-3 p-2 rounded-lg transition-all group/row",
        "bg-muted/30 border border-transparent hover:border-border hover:bg-card/50"
      )}
    >
      {/* Thumbnail */}
      {thumbnail && (
        <div className="h-8 w-8 rounded-md overflow-hidden bg-card border border-border shrink-0">
          <img
            src={`${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(thumbnail)}&w=64&token=${token}`}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-foreground truncate group-hover/row:text-primary transition-colors">
          {text !== 'No content' ? text : post.game?.name || 'Post'}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {type === 'scheduled' ? (
            <span className="text-[9px] font-bold text-amber-500/70">
              {moment(post.schedules[0]?.scheduledAt).format('ddd D, HH:mm')}
            </span>
          ) : (
            <span className="text-[9px] font-bold text-muted-foreground">
              {moment(post.schedules[0]?.scheduledAt).format('ddd D')}
            </span>
          )}
          {type === 'sent' && (
            <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
              <Heart className="h-2 w-2 text-red-500/40" />
              <span className="font-bold">{post.schedules.reduce((s, sc) => s + sc.likes, 0)}</span>
              <Eye className="h-2 w-2 text-primary/40 ml-1" />
              <span className="font-bold">{post.schedules.reduce((s, sc) => s + sc.views, 0)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Platform status badge */}
      <div className="shrink-0" onClick={(e) => e.preventDefault()}>
        <PublishStatusBadge platforms={post.schedules.map(s => ({
          platform: s.platform,
          status: s.status,
          handle: s.handle,
          accountName: s.accountName,
          externalPostId: s.externalPostId,
          externalPostUrl: s.externalPostUrl,
          result: s.result,
          scheduledAt: s.scheduledAt,
        }))} />
      </div>
    </Link>
  );
}
